import { FC, useContext, useState, useCallback, useRef } from 'react';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
    KeyboardSensor,
    DragOverlay,
    DragStartEvent,
    pointerWithin,
} from '@dnd-kit/core';
import {
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
    Empty,
    Splitter,
    message,
    theme,
    Select,
    Tooltip
} from 'antd';
import {
    Button,
    Collapse,
} from 'antd';
import { DeleteOutlined, EyeOutlined, DownOutlined } from '@ant-design/icons';

import FlexBox from '../atoms/FlexBox';
import FlexCol from '../atoms/FlexCol';
import { LayoutEditorContext } from '../../providers/editor/LayoutEditorContext';
import { UserContext } from '../../providers/UserContext';
import DeleteStructureConfirm from './DeleteStructureConfirm';
import { LayoutEditorElement } from './LayoutEditorElement';
import { PreviewEntityProvider } from '../../providers/editor/PreviewEntityProvider';
import { PreviewFormMount } from '../../providers/editor/PreviewFormMount';
import Entity from '../Entity';
import MetaInfo from '../atoms/MetaInfo';
import { findElementById } from '../../reducers/layoutEditorReducer';
import { useOrganizationParameters } from '../../use/useOrganizationParameters';

/**
 * @description State for the drop indicator (temporary for visual feedback).
 */
export interface DropIndicatorProps {
    targetElementId: string | null;
    placement: 'BEFORE' | 'INSIDE' | 'AFTER' | null;
    parentOrientation: 'HORIZONTAL' | 'VERTICAL';
}

/**
 * @description Minimal data for drag state
 */
interface DragState {
    id?: string;
    width?: number;
    height?: number;
}

/**
 * Main layout editor component that provides a drag-and-drop interface for building form layouts.
 * Supports nested containers, fields, and entity references with real-time preview.
 * Includes state management for selection, deletion, and layout structure.
 * @component
 */
export const LayoutEditor: FC = () => {
    const { token } = theme.useToken();
    const editor = useContext(LayoutEditorContext);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [dragState, setDragState] = useState<DragState>({});
    const { allVisibilityTypes } = useOrganizationParameters();
    const previewContentRef = useRef<HTMLDivElement>(null);

    if (!editor) throw new Error('Must be used within LayoutEditorProvider');
    const {
        layout,
        previewMode,
        previewVisibilityMode,
        setPreviewVisibilityMode,
        deleteStructure,
        selectedStructureId,
        structure,
        type,
        dropElement,
        selectElement,
        selectedElement,
    } = editor;
    const { userRights } = useContext(UserContext);

    const artifactName = structure?.description?.shortLabel || selectedStructureId || '';
    const requiredRight = type === 'entity' ? 'recordsAdmin' : 'formsArchive';
    const canDelete = !!userRights[requiredRight];
    const deleteDisabledReason = !canDelete ? `Requires the "${requiredRight}" permission` : undefined;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    /**
     * @description Handles drag start: captures essential element data and dimensions
     */
    const handleDragStart = useCallback((event: DragStartEvent) => {
        const activeId = event.active.id.toString();
        const activeElement = document.getElementById(activeId);
        
        if (activeElement) {
            const { width, height } = activeElement.getBoundingClientRect();
            setDragState({ 
                id: activeId,
                width,
                height
            });
        }
    }, []);

    /**
     * @description Handles drag end: validates and calls context's dropElement.
     */
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) {
            setDragState({});
            return;
        }

        const activeId = active.id.toString();
        const overId = over.id.toString();
        const overData = over.data.current;

        if (activeId === overId) {
            setDragState({});
            return;
        }
        if (overData?.isContainer) {
            const potentialParentNode = findElementById({ structure: layout.structure }, overId);
            
            const isDescendant = potentialParentNode ?
                activeId === potentialParentNode.dndId || 
                    findElementById({ structure: potentialParentNode }, activeId) !== null :
                    false;
            
            if (isDescendant) {
                setDragState({});
                return;
            }
        }

        dropElement(active, over);
        setDragState({});
    };

    /**
     * @description Handles drag cancel.
     */
    const handleDragCancel = useCallback(() => {
        setDragState({});
    }, []);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteStructure(selectedStructureId || '');
            messageApi.success(`${type === 'form' ? 'Form' : 'Record'} deleted successfully`);
            setIsDeleteModalOpen(false);
        } catch (error) {
            console.error('Error deleting:', error);
            messageApi.error(error instanceof Error ? error.message : `Failed to delete ${type === 'form' ? 'form' : 'record'}`);
        } finally {
            setIsDeleting(false);
        }
    };

    if (!selectedStructureId) {
        return (
            <FlexBox stretch justifyCenter>
                <Empty description={`Select or create a ${type === 'form' ? 'Form' : 'Record'} to begin editing.`} />
            </FlexBox>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            autoScroll={{ layoutShiftCompensation: false }}
            collisionDetection={pointerWithin}
        >
            {contextHolder}
            <Splitter
                orientation='horizontal'
                style={{
                    display: 'flex',
                    flexGrow: 1,
                }}
            >
                <Splitter.Panel
                    style={{ paddingRight: 10 }}
                    min='30%'
                >
                    {layout.structure && (
                        <LayoutEditorElement
                            element={layout.structure}
                            parentElement={null}
                            isSelected={selectedElement?.dndId === layout.structure.dndId}
                            onSelect={() => selectElement(layout.structure)}
                        />
                    )}
                    <MetaInfo />
                    <Collapse
                        size="small"
                        style={{ width: '100%' }}
                        bordered={false}
                        items={[
                            {
                                key: 'danger',
                                label: 'Danger Zone',
                                children: (
                                    <Tooltip title={deleteDisabledReason}>
                                        <Button
                                            danger
                                            type="primary"
                                            icon={<DeleteOutlined />}
                                            onClick={() => setIsDeleteModalOpen(true)}
                                            loading={isDeleting}
                                            disabled={!selectedStructureId || !canDelete}
                                        >
                                            Delete {type === 'form' ? 'Form' : 'Record'} Structure
                                        </Button>
                                    </Tooltip>
                                ),
                            }
                        ]}
                    />
                    <DragOverlay
                        style={{
                            width: dragState.width,
                            height: dragState.height,
                            border: '1px dashed ' + token['magenta-7'],
                            backgroundColor: token['magenta-2'],
                            opacity: 0.5,
                            borderRadius: token.borderRadiusLG,
                        }}
                    />
                    <DeleteStructureConfirm
                        open={isDeleteModalOpen}
                        kind={type === 'entity' ? 'entity' : 'form'}
                        artifactName={artifactName}
                        loading={isDeleting}
                        onCancel={() => setIsDeleteModalOpen(false)}
                        onConfirm={handleDelete}
                    />
                </Splitter.Panel>
                {previewMode !== 'off' && (
                    <Splitter.Panel
                        style={{ paddingLeft: 10 }}
                        min='30%'
                    >
                        <FlexCol justifyEnd alignEnd>
                            <Select
                                value={previewVisibilityMode}
                                onChange={setPreviewVisibilityMode}
                                style={{ width: 150 }}
                                size="small"
                                options={allVisibilityTypes.map(opt => ({
                                    value: opt.id,
                                    label: opt.shortLabel ?? opt.id,
                                    title: opt.longDescription ?? opt.shortLabel ?? opt.id
                                }))}
                                placeholder="Visibility"
                                prefix={<EyeOutlined style={{ pointerEvents: 'none' }} />}
                                suffix={<DownOutlined style={{ pointerEvents: 'none' }} />}
                                labelRender={(props) => (
                                    <span style={{ pointerEvents: 'none' }}>
                                        {props.label}
                                    </span>
                                )}
                            />
                        </FlexCol>
                        {type === 'form' ? (
                            <PreviewFormMount contentRef={previewContentRef} />
                        ) : (
                            <PreviewEntityProvider>
                                <Entity />
                            </PreviewEntityProvider>
                        )}
                    </Splitter.Panel>
                )}
            </Splitter>
        </DndContext>
    );
}; 